/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';
import { defaultMemoize } from 'reselect';
import { oc } from 'ts-optchain';

import { CalendarItem } from 'utils/calendarItems';
import { core, safeEnumCompare, safeToNumber } from '@tock/coretypes';
import { dashboard } from 'types';
import { filter, find, forEach, isEmpty, map, size, sum, toInteger, uniq } from 'es-toolkit/compat';
import { getBraintreeTransactionUrl, hasBraintreeTransaction } from 'utils/braintreeHelpers';
import { getStripeTransactionUrl, hasStripeTransaction } from 'utils/stripeHelpers';
import { getTagsForProfile } from './tagUtils';
import AppSettings from 'settings/AppSettings';
import protos from 'utils/protos';

const { PastPurchase } = protos;
export interface PurchasedOptionInfo {
  id: number;
  count: number;
  amount: number;
  name: string;
  chitName: string;
  ticketOptionGroupId: number;
  modifier: boolean;
}

// TODO [BRM 2020/11/09] handle id collisions between menu items and modifier options
export function createOptionMap(purchasedOptions: core.IPurchasedOption[] | undefined) {
  const priceMap: Dictionary<PurchasedOptionInfo> = {};
  if (!purchasedOptions || purchasedOptions.length === 0) {
    return priceMap;
  }

  let uniqueKey = 0;
  forEach(purchasedOptions, (option) => {
    const hasModifiers = size(option.modifierOption) > 0;
    if (!hasModifiers) {
      const key = 'key-o-' + (option.id ?? 0);
      if (priceMap[key]) {
        priceMap[key].count = priceMap[key].count + 1;
      } else {
        priceMap[key] = {
          id: option.id ?? 0,
          name: option.name ?? '',
          chitName: option.chitName ?? '',
          count: 1,
          amount: option.amount ?? 0,
          ticketOptionGroupId: option.ticketOptionGroupId ?? 0,
          modifier: false,
        };
      }
      return;
    }
    const key = 'key-' + ++uniqueKey;
    priceMap[key] = {
      id: option.id ?? 0,
      name: option.name ?? '',
      chitName: option.chitName ?? '',
      count: 1,
      amount: option.amount ?? 0,
      ticketOptionGroupId: option.ticketOptionGroupId ?? 0,
      modifier: false,
    };

    forEach(option.modifierOption, (modifier) => {
      const modifierKey = key + '-m-' + (modifier.id ?? 0);
      if (priceMap[modifierKey]) {
        priceMap[modifierKey].count = priceMap[modifierKey].count + 1;
      } else {
        priceMap[modifierKey] = {
          id: modifier?.id ?? 0,
          name: modifier?.name ?? '',
          chitName: modifier.chitName ?? '',
          count: 1,
          amount: modifier?.amount ?? 0,
          ticketOptionGroupId: modifier.ticketOptionGroupId ?? 0,
          modifier: true,
        };
      }
    });
  });

  return priceMap;
}

export const getExistingEmailsForPurchase = defaultMemoize(
  (purchase: core.IPastPurchase): string[] => {
    return filter(
      uniq(
        map(purchase?.dinerPatron || [], (patron) => patron?.email || '').concat([
          purchase?.ownerPatron?.email || '',
        ])
      ),
      (p) => !!p
    );
  }
);

export enum NOTESCOPE {
  BUSINESS_GROUP = 'BUSINESS_GROUP',
  BUSINESS = 'BUSINESS',
}

/**
 * Checks whether a purchase has an unanswered survey.
 * Checks that the ticket type has questions that need to be answered, and that they haven't been answered
 * Also checks that the purchase hasn't been cancelled, and that the purchase is for a table in the future.
 *
 * @param purchase PastPurchase
 * @returns boolean Whether or not the purchase has unanwered questions
 */
export function hasUnansweredSurvey(purchase?: core.PastPurchase) {
  return (
    purchase &&
    !isEmpty(oc(purchase).ticketType.question([])) &&
    isEmpty(purchase.question) &&
    !purchase.cancelledOrRefunded &&
    LocalDateTime.now().isBefore(LocalDateTime.parse(purchase.ticketDateTime))
  );
}

export function isUnlisted(calendarItem: CalendarItem) {
  return calendarItem.purchase
    ? oc(calendarItem.purchase).ticketType.unlisted()
    : calendarItem.ticketGroup.unlisted;
}

export function guestOrGroupGuestNotes(
  guestProfile: dashboard.IGuestProfile,
  activeBusinessId: number
) {
  if (guestNotes(guestProfile, 'GENERAL', activeBusinessId)) {
    return guestNotes(guestProfile, 'GENERAL', activeBusinessId);
  } else {
    return guestGroupNotes(guestProfile, 'GENERAL');
  }
}

export function guestDietaryOrGroupGuestDietaryNotes(
  guestProfile: dashboard.IGuestProfile,
  activeBusinessId: number,
  scope: NOTESCOPE
) {
  if (scope === NOTESCOPE.BUSINESS_GROUP) {
    return guestGroupNotes(guestProfile, 'DIETARY');
  } else {
    return guestNotes(guestProfile, 'DIETARY', activeBusinessId);
  }
}

export function visitGeneralNotes(purchase: core.PastPurchase): dashboard.AuditedNote | undefined {
  return visitNotes(purchase, 'GENERAL');
}

export function visitDietaryNotes(purchase: core.PastPurchase): dashboard.AuditedNote | undefined {
  return visitNotes(purchase, 'DIETARY');
}

/**
 * Get a dietary or general visit note object
 *
 * @param purchase PastPurchase
 * @param noteType string of 'GENERAL' or 'DIETARY'
 * @returns Note object
 */
function visitNotes(
  purchase: core.PastPurchase,
  noteType: string
): dashboard.AuditedNote | undefined {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return find(oc(purchase).auditedBookingNote() as dashboard.AuditedNote[], (note) => {
    return !isEmpty(note.note) && note.noteType === noteType;
  })!;
}

/**
 * Get a dietary or general RESTAURANT LEVEL guest note object
 *
 * @param purchase PastPurchase
 * @param noteType string of 'GENERAL' or 'DIETARY'
 * @param activeBusinessId number id for the active business
 * @returns Note object
 */
function guestNotes(
  guestProfile: dashboard.IGuestProfile,
  noteType: string,
  activeBusinessId: number
): dashboard.AuditedNote {
  const businesses = oc(guestProfile).businessGuestProfile();
  const business = find(businesses, (business) => {
    return safeToNumber(oc(business).business.id()) === safeToNumber(activeBusinessId);
  });

  if (business && !isEmpty(business)) {
    return find(business.note as dashboard.AuditedNote[], (note) => {
      return note.noteType === noteType && note.note !== '';
    }) as dashboard.AuditedNote;
  }

  return '' as any;
}

/**
 * Get a dietary or general GROUP LEVEL guest note object
 *
 * @param purchase PastPurchase
 * @param noteType string of 'GENERAL' or 'DIETARY'
 * @returns Note object
 */
function guestGroupNotes(
  guestProfile: dashboard.IGuestProfile,
  noteType: any
): dashboard.AuditedNote {
  const businessGroup = oc(guestProfile).businessGroupGuestProfile();

  if (businessGroup) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return find(businessGroup.note as dashboard.AuditedNote[], (groupNote) => {
      return groupNote.noteType === noteType && groupNote.note !== '';
    })!;
  }

  return '' as any;
}

export function isImported(purchase: core.PastPurchase) {
  if (!purchase) {
    return false;
  }
  return (
    !oc(purchase).ticketType.isBookable() ||
    safeEnumCompare(
      oc(purchase).originatedAt(),
      PastPurchase.OriginatedAt,
      PastPurchase.OriginatedAt.IMPORT
    )
  );
}

export function hasIcons(
  purchase: core.PastPurchase,
  activeBusiness: core.Business,
  useGroupDietaryNote: boolean
): boolean {
  const visit = oc(purchase).visit({});

  const visitTags = oc(visit).tag([]);
  const guestTags = getTagsForProfile(oc(purchase).dinerGuestProfile());

  const hasTags = !isEmpty(visitTags) || !isEmpty(guestTags);

  const total = oc(purchase).totalPrice(0);
  const amountPaidCents = oc(purchase).amountPaidCents(0);

  const hasPaymentRequired = total - amountPaidCents > 0;

  const hasGuestNote = !isEmpty(guestOrGroupGuestNotes(purchase, toInteger(activeBusiness.id)));
  const hasVisitNote = !isEmpty(visitGeneralNotes(purchase));
  const hasVisitDietaryNote = !isEmpty(visitDietaryNotes(purchase));
  const hasGuestDietaryNote =
    purchase.dinerGuestProfile &&
    !isEmpty(
      guestDietaryOrGroupGuestDietaryNotes(
        purchase.dinerGuestProfile,
        toInteger(activeBusiness.id),
        useGroupDietaryNote ? NOTESCOPE.BUSINESS_GROUP : NOTESCOPE.BUSINESS
      )
    );

  const hasNotes = hasGuestNote || hasVisitNote || hasVisitDietaryNote || hasGuestDietaryNote;

  const hasAnsweredQuestion = !isEmpty(oc(purchase).question([]));
  const hasBeenConfirmed = oc(purchase).confirmedViaSms() || !!oc(purchase).visit.delivered();
  const hasPurchasedSupplements = !isEmpty(createOptionMap(purchase.purchasedOption));

  const isWineDirectMember = !isEmpty(
    filter(oc(purchase).dinerGuestProfile.attribute([]), { attribute: 'MembershipClubName' })
  );

  return (
    hasBeenConfirmed ||
    hasTags ||
    hasPaymentRequired ||
    hasNotes ||
    hasAnsweredQuestion ||
    hasPurchasedSupplements ||
    isWineDirectMember
  );
}

type ProcessorType = 'BRAINTREE' | 'STRIPE';
interface ProcessorLink {
  type: ProcessorType;
  link: string;
}

/**
 * Returns a ProcessorLink if ANY transaction in the purchase history is from BRAINTREE or STRIPE
 * Note: The API endpoint (/payment/link) will resolve to the original purchase if the passed
 * purchaseId itself is not a Braintree/Stripe transaction
 */
function buildPaymentProcessorLink(
  purchaseHistory: core.PurchaseChangeHistory[],
  purchaseId: number,
  businessId?: number | null
): ProcessorLink | undefined {
  if (!businessId) {
    return;
  }

  const link = `${AppSettings.API_BASE}/payment/link?purchaseId=${purchaseId}&businessId=${businessId}`;

  return hasBraintreeTransaction(purchaseHistory)
    ? { type: 'BRAINTREE', link }
    : hasStripeTransaction(purchaseHistory)
      ? { type: 'STRIPE', link }
      : undefined;
}

/**
 * This function constructs a link if ANY transaction in the purchase
 * chain was from Braintree or Stripe
 */
export function getProcessorLinkFromPastPurchase(
  purchase: core.PastPurchase
): ProcessorLink | undefined {
  const { changeHistory, id, business } = purchase;

  return buildPaymentProcessorLink(changeHistory as core.PurchaseChangeHistory[], id, business?.id);
}

/**
 * takes a purchase and looks through a ticket group to get that same purchase
 * based on originalPurchaseId
 */
export function getCurrentPurchaseFromTicketGroup(
  ticketGroup: core.TicketGroup,
  originalPurchaseId: number
): core.PastPurchase | undefined {
  const currentPurchase = find(
    ticketGroup.purchaseDetail,
    (purchaseDetails) =>
      safeToNumber(purchaseDetails.originalPurchaseId) === safeToNumber(originalPurchaseId)
  );
  return currentPurchase as core.PastPurchase;
}

/**
 * Takes a past purchase and returns the amount of loyalty program points redeemed.
 */
export function getLoyaltyPointsRedeemed(purchase: core.PastPurchase): number {
  return sum(
    map(purchase.discount, (discount) => (discount.loyaltyProgram ? discount.amountCents : 0))
  );
}

export interface PaymentProcessorLinkParams {
  activeBusiness: core.Business;
  changeHistorys?: core.PurchaseChangeHistory[];
  confirmationId?: string;
  isEngOrHospitality: boolean;
  processorType: core.PaymentProcessorType;
}

export function getPaymentProcessorLink({
  activeBusiness,
  changeHistorys,
  confirmationId,
  isEngOrHospitality,
  processorType,
}: PaymentProcessorLinkParams): JSX.Element | undefined {
  if (!isEngOrHospitality || !changeHistorys || !confirmationId || !processorType) {
    return undefined;
  }

  const isBraintree = hasBraintreeTransaction(changeHistorys);
  if (isBraintree) {
    const transactionUrl = getBraintreeTransactionUrl(
      changeHistorys,
      confirmationId,
      activeBusiness.id
    );
    if (transactionUrl) {
      return (
        <span>
          &nbsp;(
          <a href={transactionUrl} target="_blank" rel="noopener noreferrer">
            Braintree
          </a>
          )
        </span>
      );
    }
  } else {
    const transactionUrl = getStripeTransactionUrl(
      changeHistorys,
      confirmationId,
      activeBusiness.id
    );
    if (transactionUrl) {
      return (
        <span>
          &nbsp;(
          <a href={transactionUrl} target="_blank" rel="noopener noreferrer">
            Stripe
          </a>
          )
        </span>
      );
    }
  }
  return undefined;
}

export function isTableside(purchase?: core.PastPurchase) {
  if (!purchase) {
    return false;
  }

  return (
    purchase.originatedAt === 'VISIT_EDIT' ||
    purchase.originatedAt === 'VISIT_EDIT_DASHBOARD' ||
    !!purchase.visitEditToken
  );
}
